import { useEffect } from 'react';

import { ToastAction } from '@shared';

import { t } from '@/i18n';
import { useToast } from '@/hooks/use-toast';

/**
 * TRAD-3045 — 거래소 WebSocket이 재연결(3·6·12·24·48초 백오프)을 5회 다 쓰고도 실패하면
 * 지금까지는 아무 알림 없이 조용히 포기했다("숫자가 굳었는데 굳은 줄 모른다"). 그 신호
 * (`connection_gave_up`/onGaveUp)를 받아 재시도 버튼이 달린 토스트를 띄운다.
 *
 * upbit/bithumb/binance 세 훅이 동일한 문구·동작을 그대로 재사용한다(UI 일관성 룰 1 —
 * 새 배너 컴포넌트를 만들지 않고 기존 토스트+ToastAction 패턴을 재사용, `useLoginRequired`와
 * 같은 자리).
 *
 * 🔴 좀비 연결 감지(PONG_TIMEOUT 기반 워치독)는 이 경로를 안 탄다 — 워치독은 스스로 계속
 * 재시도하며, 관측 n=3(발현까지 44초/9분2초/59초 — 전부 강제 재연결로 즉시 복구)에서는
 * 매번 성공했으므로(표본이 셋뿐이라 "항상"은 과장이다 — TRAD-3045 조사) 의도적으로 포기
 * 없이 무한 재시도한다. 여기서 다루는 건 **진짜 네트워크 단절**처럼 backoff 재연결 자체가
 * 반복 실패하는 경우다.
 */
interface GaveUpCapableClient {
  onGaveUp(listener: () => void): () => void;
  retry(): void;
}

export const useRealtimeGaveUpToast = (client: GaveUpCapableClient, enabled: boolean) => {
  const { toast } = useToast();

  useEffect(() => {
    if (!enabled) return;

    return client.onGaveUp(() => {
      toast({
        description: t('market.realtime.connection_lost'),
        action: (
          <ToastAction altText={t('market.realtime.retry')} onClick={() => client.retry()}>
            {t('market.realtime.retry')}
          </ToastAction>
        ),
      });
    });
  }, [client, enabled, toast]);
};
